import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { C, alpha } from '../lib/theme.js';

/**
 * Toasts — the app's single answer to "did that work?".
 *
 * House rule: every button that makes a server call shows animated progress while
 * it runs AND lands a toast when it finishes — success or failure, always. Silence
 * is never an acceptable outcome; an operator should never have to guess whether a
 * campaign built, a post saved, or a sync ran.
 *
 * Errors linger (you have to be able to read them and they're dismissible);
 * successes are a brief confirmation and fade.
 */

export type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  toast: (kind: ToastKind, message: string) => void;
}

const ToastCtx = createContext<ToastApi>({ toast: () => {} });

/** Fire a toast: `const { toast } = useToast(); toast('error', 'Build failed — …')`. */
export function useToast(): ToastApi {
  return useContext(ToastCtx);
}

const TONE: Record<ToastKind, string> = {
  success: C.good,
  error: C.act,
  info: C.basil,
};
const GLYPH: Record<ToastKind, string> = { success: '✓', error: '!', info: '·' };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const remove = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (kind: ToastKind, message: string) => {
      const id = ++seq.current;
      setItems((list) => [...list, { id, kind, message }]);
      // Failures stay up long enough to actually read; successes get out of the way.
      window.setTimeout(() => remove(id), kind === 'error' ? 10000 : 4500);
    },
    [remove],
  );

  const api = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      {/* Bottom-centre: clear of the left nav and the Basil bot bubble (bottom-right). */}
      <div
        style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          zIndex: 9999,
          pointerEvents: 'none',
          width: 'min(560px, calc(100vw - 48px))',
        }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            style={{
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              width: '100%',
              padding: '11px 13px',
              borderRadius: 10,
              background: C.surface,
              border: `1px solid ${alpha(TONE[t.kind], 45)}`,
              boxShadow: `0 10px 30px rgba(0,0,0,.35)`,
              animation: 'basilfade .18s ease',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                flexShrink: 0,
                width: 17,
                height: 17,
                borderRadius: 999,
                background: alpha(TONE[t.kind], 18),
                color: TONE[t.kind],
                font: `700 10px 'IBM Plex Mono',monospace`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 1,
              }}
            >
              {GLYPH[t.kind]}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: C.primary, lineHeight: 1.5 }}>
              {t.message}
            </span>
            <span
              onClick={() => remove(t.id)}
              style={{
                flexShrink: 0,
                cursor: 'pointer',
                color: C.faint,
                fontSize: 13,
                lineHeight: 1,
                padding: '0 2px',
              }}
            >
              ×
            </span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
